import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { ToastStatus } from './toast.schema';
import Icon from '../icon';
import Button from '../button';
import { func } from '../util';
import ConfigProvider from '../config-provider';
import { Transition, motion } from './base/transition';

class ToastItem extends React.Component {
  static propTypes = {
    prefix: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    title: PropTypes.string,
    content: PropTypes.string,
    handlers: PropTypes.array,
    status: PropTypes.any,
    icon: PropTypes.string,
    closeable: PropTypes.bool,
    onClose: PropTypes.func,
    onPause: PropTypes.func,
    onStart: PropTypes.func,
    onRemove: PropTypes.func,
    onClickHandlers: PropTypes.func,
  };

  static defaultProps = {
    prefix: 'next-',
  };

  componentWillUnmount() {
    this.props.onRemove();
  }

  handleClose = () => {
    if (func.isFunction(this.props.onClose)) {
      this.props.onClose();
    }
  };

  handlePause = () => {
    if (func.isFunction(this.props.onPause)) {
      this.props.onPause();
    }
  };

  getSuffix() {
    switch (this.props.status) {
      case ToastStatus.Error:
        return 'error';
      case ToastStatus.Success:
        return 'success';
      case ToastStatus.Info:
        return 'info';
      case ToastStatus.Warning:
        return 'warning';
    }
  }

  handleStart = () => {
    if (func.isFunction(this.props.onStart)) {
      this.props.onStart();
    }
  };

  getStatusClassName(classNamePrefix) {
    const suffix = this.getSuffix();
    return cx(classNamePrefix, suffix ? `${classNamePrefix}-is-${suffix}` : null);
  }

  getStatusIconName() {
    switch (this.props.status) {
      case ToastStatus.Success:
        return 'tick-o-fill';
      case ToastStatus.Warning:
        return 'warning-o-fill';
      case ToastStatus.Error:
        return 'remove-o-fill';
      default:
        return 'info-o-fill';
    }
  }

  renderContent(toastPrefix) {
    const cls = `${toastPrefix}-content`;
    return <div className={cls}>{this.renderTitle(toastPrefix)}</div>;
  }

  renderTitle(toastPrefix) {
    const titleCls = `${toastPrefix}-title`;
    const textCls = `${toastPrefix}-title-text`;
    return (
      <div className={titleCls}>
        {this.renderIcon(toastPrefix)}
        <div className={textCls}>
          {this.renderMessage(toastPrefix)}
          {this.renderHandlers(toastPrefix)}
          {this.renderDescription(toastPrefix)}
        </div>
        {this.renderCloseHandler(toastPrefix)}
      </div>
    );
  }

  renderHandlers(toastPrefix) {
    const { handlers, onClickHandlers } = this.props;
    if (!handlers) {
      return null;
    }
    const handlerCls = `${toastPrefix}-handlers`;
    if (typeof handlers !== 'string') {
      return <span className={handlerCls}>{handlers}</span>;
    }
    return (
      <Button className={handlerCls} type="primary" text onClick={onClickHandlers}>
        {handlers}
      </Button>
    );
  }

  renderCloseHandler(toastPrefix) {
    const { closeable } = this.props;
    if (!closeable) {
      return null;
    }
    const iconCls = `${toastPrefix}-close-handler`;
    return <Icon className={iconCls} type="remove" onClick={this.handleClose} size="s" />;
  }

  renderStatusBar(toastPrefix) {
    const cls = `${toastPrefix}-solid-bar`;
    return <hr className={this.getStatusClassName(cls)} />;
  }

  renderIcon(toastPrefix) {
    if (this.props.icon !== undefined) {
      return this.props.icon;
    }
    const iconName = this.getStatusIconName();
    if (typeof iconName !== 'string') {
      return null;
    }
    const iconCls = `${toastPrefix}-symbol`;
    return <Icon className={this.getStatusClassName(iconCls)} type={iconName} />;
  }

  renderMessage(toastPrefix) {
    const { title } = this.props;
    if ((typeof title !== 'string' || title.length === 0) && typeof title !== 'object') {
      return null;
    }
    const msgCls = `${toastPrefix}-message`;
    return <h4 className={msgCls}>{title}</h4>;
  }

  renderDescription(toastPrefix) {
    const { content } = this.props;
    if ((typeof content !== 'string' || content.length === 0) && typeof content !== 'object') {
      return null;
    }
    const descCls = `${toastPrefix}-description`;
    return <p className={descCls}>{content}</p>;
  }

  render() {
    const { prefix, className, style } = this.props;
    const toastPrefix = `${prefix}toast`;

    const classes = cx({
      [`${toastPrefix}-component`]: true,
      [toastPrefix]: true,
      [className]: !!className,
    });

    return (
      <div
        className={classes}
        onMouseEnter={this.handlePause}
        onMouseLeave={this.handleStart}
        style={style}
      >
        {this.renderStatusBar(toastPrefix)}
        {this.renderContent(toastPrefix)}
      </div>
    );
  }
}

const CSSMotion = {
  enter: motion('.25s cubic-bezier(0.8, 0, 0.4, 1)', { height: '0' }, { height: '*' }),
  exit: motion('.18s cubic-bezier(0.8, 0, 0.4, 1)', { height: '*' }, { height: '0' }),
};

export default ConfigProvider.config(Transition(CSSMotion, true)(ToastItem));
